import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AgencyPermission, ModuleKey, PlatformPermission } from '@sfa/shared';
import { AuthProvider, useAuth } from '@/contexts/auth-context';
import { TenantProvider, useTenant } from '@/contexts/tenant-context';
import { UnknownHostPage } from '@/pages/UnknownHostPage';
import { ThemeProvider } from '@/app/ThemeProvider';
import { ProtectedRoute, PublicOnlyRoute } from '@/components/layout/ProtectedRoute';
import { RequirePermission } from '@/components/layout/RequirePermission';
import { SETTINGS_PERMISSIONS } from '@/features/settings/settings-sections';
import { LoginPage } from '@/pages/LoginPage';
import { DevNavPage } from '@/pages/DevNavPage';
import { usePermissions } from '@/hooks/usePermissions';
import { ReportBugWidget } from '@/features/bug-report/ReportBugWidget';
import { NotificationStream } from '@/features/notifications/NotificationStream';
import { PwaUpdateToast } from '@/features/notifications/PwaUpdateToast';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';

const ProducerDashboardPage = lazy(
  () => import('@/features/producer/ProducerDashboardPage'),
);
const ManagementDashboardPage = lazy(
  () => import('@/features/management/ManagementDashboardPage'),
);
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'));
const DataExportPage = lazy(
  () => import('@/features/data-export/DataExportPage'),
);
const CommandCenterPage = lazy(
  () => import('@/features/command-center/CommandCenterPage'),
);
const ServiceDashboardPage = lazy(
  () => import('@/features/service/ServiceDashboardPage'),
);
const TicketWorkspacePage = lazy(
  () => import('@/features/tickets/TicketWorkspacePage'),
);
const ArchivedTicketsPage = lazy(
  () => import('@/features/tickets/ArchivedTicketsPage'),
);
const HouseholdsListPage = lazy(
  () => import('@/features/household/HouseholdsListPage'),
);
const HouseholdDetailsPage = lazy(
  () => import('@/features/household/HouseholdDetailsPage'),
);
const PolicyDetailPage = lazy(
  () => import('@/features/policy/PolicyDetailPage'),
);
const LeadsPage = lazy(() => import('@/features/lead/LeadsPage'));
const NewLeadPage = lazy(() => import('@/features/lead/NewLeadPage'));
const NewQuoteRecapPage = lazy(
  () => import('@/features/quote-recap/NewQuoteRecapPage'),
);
const EditQuoteRecapPage = lazy(
  () => import('@/features/quote-recap/EditQuoteRecapPage'),
);
/**
 * One page, one mode: a sale on a lead. A Cancel Rewrite or Company Transfer
 * is the same sale on a lead stamped with what it replaces (PAC-126) — the
 * server reads that from the lead, so the page has no mode switch to make.
 */
const SoldDealPage = lazy(() => import('@/features/sold/SoldDealPage'));
const EditSoldDealPage = lazy(
  () => import('@/features/sold/EditSoldDealPage'),
);
const RewriteRouteRedirect = lazy(
  () => import('@/features/sold/RewriteRouteRedirect'),
);
const PublicLeadFormPage = lazy(
  () => import('@/features/lead/PublicLeadFormPage'),
);
const LeadDetailsPage = lazy(() => import('@/features/lead/LeadDetailsPage'));
const RolePermissionsPage = lazy(
  () => import('@/features/admin/RolePermissionsPage'),
);
const UsersPage = lazy(() => import('@/features/admin/UsersPage'));
const BrandingPage = lazy(() => import('@/features/settings/BrandingPage'));
const AgencyProfilePage = lazy(
  () => import('@/features/settings/AgencyProfilePage'),
);
const DomainsPage = lazy(() => import('@/features/settings/DomainsPage'));
const EmailSenderPage = lazy(
  () => import('@/features/settings/EmailSenderPage'),
);
const CarrierAppointmentsPage = lazy(
  () => import('@/features/settings/CarrierAppointmentsPage'),
);
const SuperAdminHomePage = lazy(
  () => import('@/features/platform/SuperAdminHomePage'),
);
const OnboardAgencyPage = lazy(
  () => import('@/features/platform/onboard/OnboardAgencyPage'),
);
const AgencySetupPage = lazy(
  () => import('@/features/agency-setup/AgencySetupPage'),
);
const PlatformUsersPage = lazy(
  () => import('@/features/platform/PlatformUsersPage'),
);
const ImpersonateHandoffPage = lazy(
  () => import('@/pages/ImpersonateHandoffPage')
);
const BugReportsPage = lazy(
  () => import('@/features/platform/BugReportsPage'),
);
const MailerCampaignsPage = lazy(
  () => import('@/features/platform/campaigns/MailerCampaignsPage'),
);
const RunCampaignPage = lazy(
  () => import('@/features/platform/campaigns/RunCampaignPage'),
);
const ZipMarketsPage = lazy(
  () => import('@/features/platform/campaigns/ZipMarketsPage'),
);
const MailerCampaignDetailPage = lazy(
  () => import('@/features/platform/campaigns/MailerCampaignDetailPage'),
);
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ProfilePage = lazy(() => import('@/features/settings/ProfilePage'));
const NotificationsPage = lazy(
  () => import('@/features/notifications/NotificationsPage'),
);
const WorkspaceSettingsPage = lazy(
  () => import('@/features/settings/WorkspaceSettingsPage'),
);
const UserPermissionsPage = lazy(
  () => import('@/features/admin/UserPermissionsPage'),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

/**
 * Short-circuits the whole app when the hostname serves no tenant.
 *
 * Placed **inside** `TenantProvider` (it needs the resolution) but **outside**
 * `AuthProvider` and the router, so an unrecognised host never reaches a login
 * form or a route at all — see `UnknownHostPage` for why that matters.
 *
 * Renders children while the first fetch is in flight rather than blocking on
 * it: gating every cold load on a network round-trip to spare a mistyped domain
 * one frame of the login page is the wrong trade.
 */
function TenantGate({ children }: { children: React.ReactNode }) {
  const { unknownHost } = useTenant();
  return unknownHost ? <UnknownHostPage /> : <>{children}</>;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
      Loading…
    </div>
  );
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * Role-based landing for `/`. Each user is sent to the most relevant
 * dashboard for their permissions. Owners/managers land on the
 * management dashboard, producers on the producer dashboard, etc.
 * Anyone without an obvious dashboard (e.g. platform admin) gets the
 * navigation hub.
 */
function RoleLanding() {
  const { canRead, can } = usePermissions();
  const { user } = useAuth();

  // A platform operator holds only `platform:*` and no module permissions, so
  // without this they fall all the way through to the dev navigator — a page
  // full of tenant dashboards they cannot open (PAC-73).
  if (can(PlatformPermission.AgenciesRead)) {
    return <Navigate to="/admin" replace />;
  }
  /*
   * An owner who has not finished their agency's first-run setup goes there
   * first (PAC-69) — the case being an owner who closed the tab partway through
   * the invite wizard, who would otherwise have no route back to it.
   *
   * Server-resolved on every `/auth/me`, not a client guess, and it clears the
   * moment they finish *or skip*: skipping completes the setup precisely so this
   * redirect cannot become a thing they have to dismiss on every sign-in.
   */
  if (user?.agencySetupPending) {
    return <Navigate to="/welcome/agency" replace />;
  }
  if (canRead(ModuleKey.Management)) {
    return <Navigate to="/dashboard/management" replace />;
  }
  if (canRead(ModuleKey.Dashboard)) {
    return <Navigate to="/dashboard/producer" replace />;
  }
  if (canRead(ModuleKey.CrmService)) {
    return <Navigate to="/crm/service" replace />;
  }
  if (canRead(ModuleKey.Clients)) {
    return <Navigate to="/clients" replace />;
  }
  if (canRead(ModuleKey.Leads)) {
    return <Navigate to="/leads" replace />;
  }
  return <DevNavPage />;
}

export function App() {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      {/* Radix tooltips need a provider ancestor or `Tooltip.Root` throws at
          render — and `components/ui/tooltip` deliberately does not self-wrap,
          because the open/close delays are a global concern. Mounted once here
          rather than per call site (first needed by the dashboard's lead quick
          actions, PAC-16). */}
      <TooltipProvider delayDuration={200}>
        {/* Above `AuthProvider` on purpose: the login and accept-invite pages
            are branded, and both render before anyone is authenticated. */}
        <TenantProvider>
        <TenantGate>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            <Route element={<PublicOnlyRoute />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<RoleLanding />} />
              <Route path="/nav" element={<DevNavPage />} />

              {/*
                No layout route here: the shell is `AppShell`, which each page
                renders itself so it can put `MobileNav` inside its own header
                instead of stacking a second app bar above it. A layout route
                would render `AppSidebar` a second time around pages that
                already have one. Each page still declares its own per-page
                `RequirePermission` gate.
              */}
              <>
                <Route
                  element={
                    <RequirePermission permission={`${ModuleKey.Dashboard}:read`} />
                  }
                >
                  <Route
                    path="/dashboard/producer"
                    element={
                      <LazyPage>
                        <ProducerDashboardPage />
                      </LazyPage>
                    }
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={`${ModuleKey.CrmService}:read`} />
                  }
                >
                  <Route
                    path="/crm/service"
                    element={
                      <LazyPage>
                        <ServiceDashboardPage />
                      </LazyPage>
                    }
                  />
                  <Route
                    path="/crm/tickets"
                    element={
                      <LazyPage>
                        <TicketWorkspacePage />
                      </LazyPage>
                    }
                  />
                  <Route
                    path="/crm/tickets/archived"
                    element={
                      <LazyPage>
                        <ArchivedTicketsPage />
                      </LazyPage>
                    }
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={`${ModuleKey.Leads}:read`} />
                  }
                >
                  <Route
                    path="/leads"
                    element={
                      <LazyPage>
                        <LeadsPage />
                      </LazyPage>
                    }
                  />
                  {/* Static segment, so it wins over `/leads/:id` regardless of
                      order — declared first for readability. Needs `leads:write`,
                      which is stricter than the surrounding read gate. */}
                  <Route
                    element={
                      <RequirePermission
                        permission={`${ModuleKey.Leads}:write`}
                        redirectTo="/leads"
                      />
                    }
                  >
                    <Route
                      path="/leads/new"
                      element={
                        <LazyPage>
                          <NewLeadPage />
                        </LazyPage>
                      }
                    />
                  </Route>
                  {/* `/leads/demo` was removed with PAC-38: the page renders real
                      data now, so a route that could only ever show the mockup was
                      dead weight. It had no inbound links, and the `*` catch-all
                      below handles a stale bookmark. */}
                  <Route
                    path="/leads/:id"
                    element={
                      <LazyPage>
                        <LeadDetailsPage />
                      </LazyPage>
                    }
                  />
                </Route>
              </>

              <Route
                element={
                  <RequirePermission permission={`${ModuleKey.Management}:read`} />
                }
              >
                <Route
                  path="/dashboard/management"
                  element={
                    <LazyPage>
                      <ManagementDashboardPage />
                    </LazyPage>
                  }
                />
                {/* The Agency Command Center (PAC-138). Still on
                    `management:read` with the Overview above it: the route's
                    audience has not changed, and `command_center:read` exists
                    but is held by Data Team, who have no leads permissions at
                    all and would reach a page of empty panels. */}
                <Route
                  path="/dashboard/management-alt"
                  element={
                    <LazyPage>
                      <CommandCenterPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* The Data Export page (PAC-152). Its own module, so the owner
                  grants it from the role matrix like any page; the Data Team
                  role holds it by default. */}
              <Route
                element={
                  <RequirePermission permission={`${ModuleKey.DataExport}:read`} />
                }
              >
                <Route
                  path="/data-export"
                  element={
                    <LazyPage>
                      <DataExportPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* The Analytics page (PAC-152, part 2). The owner holds it
                  through enabled modules, the Branch Manager by template; the
                  owner grants it to anyone else from the role matrix. */}
              <Route
                element={
                  <RequirePermission permission={`${ModuleKey.Analytics}:read`} />
                }
              >
                <Route
                  path="/analytics"
                  element={
                    <LazyPage>
                      <AnalyticsPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* The mock demo page stays behind the Clients page permission.
                  No sidebar entry points here any more (see AppSidebar) — it is
                  reachable from the dev Screen Navigator at `/`.
                  Declared before `/clients/:id` so the literal segment wins. */}
              <Route
                element={
                  <RequirePermission permission={`${ModuleKey.Clients}:read`} />
                }
              >
                {/* The Clients list (PAC-89). `clients:read` only — unlike
                    `/clients/:id` below, which a CSR also reaches from a ticket.
                    That split mirrors the API: the index requires
                    `clients:read`, the record accepts either page permission. */}
                <Route
                  path="/clients"
                  element={
                    <LazyPage>
                      <HouseholdsListPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="/clients/demo"
                  element={
                    <LazyPage>
                      <HouseholdDetailsPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* Household and policy records also render inside the CRM
                  ticket detail, so either page permission grants access. */}
              <Route
                element={
                  <RequirePermission
                    anyOf={[
                      `${ModuleKey.Clients}:read`,
                      `${ModuleKey.CrmService}:read`,
                    ]}
                  />
                }
              >
                <Route
                  path="/clients/:id"
                  element={
                    <LazyPage>
                      <HouseholdDetailsPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="/policies/:id"
                  element={
                    <LazyPage>
                      <PolicyDetailPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* Cancel & rewrite used to live here as a page of its own, then
                  briefly as a mode of the Sold form. It is now a two-form chain
                  — New Lead, then Sold — started from a button on the policy or
                  household page, so no URL can express "start a rewrite" and
                  this only redirects to the policy. Kept because the old URL
                  was linked from both pages, so it is in history and bookmarks.

                  The gate is kept rather than left to the policy page: it is
                  the chain's permission (`deal_audits:write`), and gating here
                  means an unauthorized caller lands back on /clients instead of
                  bouncing through the redirect. */}
              <Route
                element={
                  <RequirePermission
                    permission={`${ModuleKey.DealAudits}:write`}
                    redirectTo="/clients"
                  />
                }
              >
                <Route
                  path="/policies/:policyId/rewrite"
                  element={
                    <LazyPage>
                      <RewriteRouteRedirect />
                    </LazyPage>
                  }
                />
              </Route>

              {/* Quote Recap form (PAC-39). Deliberately NOT nested under the
                  `leads:read` gate: every endpoint this page calls (context,
                  presign, create) sits behind `quote_recaps`, so one gate covers
                  the whole flow rather than letting a user pass the outer check
                  and fail mid-form. */}
              <Route
                element={
                  <RequirePermission
                    permission={`${ModuleKey.QuoteRecaps}:write`}
                    redirectTo="/leads"
                  />
                }
              >
                <Route
                  path="/quote-recaps/new"
                  element={
                    <LazyPage>
                      <NewQuoteRecapPage />
                    </LazyPage>
                  }
                />
                {/* Editing a recorded recap (PAC-56 #11). Same gate: every
                    endpoint it calls — GET :id, the presign, PATCH :id — sits
                    behind the same module. */}
                <Route
                  path="/quote-recaps/:id/edit"
                  element={
                    <LazyPage>
                      <EditQuoteRecapPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* Sold form (PAC-40), always on a lead — `?leadId=`. A
                  replacement (PAC-126) is the same form on a lead stamped with
                  the policy it replaces. Gated on `deal_audits:write` because
                  that is what POST /sold-deals requires, so the route and the
                  API agree.

                  Note PAC-38 has since added `clients:write` to the Producer
                  template (for editing a lead's contact), so the original
                  reasoning — "producers hold no `clients:*`" — no longer holds.
                  The gate stays where it is regardless: it matches the endpoint,
                  which is the durable reason. */}
              <Route
                element={
                  <RequirePermission
                    permission={`${ModuleKey.DealAudits}:write`}
                    redirectTo="/leads"
                  />
                }
              >
                <Route
                  path="/sold/new"
                  element={
                    <LazyPage>
                      <SoldDealPage />
                    </LazyPage>
                  }
                />
                {/* Editing a booked sale (PAC-104). Same gate: PATCH
                    /sold-deals/:id and POST /sold-deals/:id/policies both
                    require `deal_audits:write`. */}
                <Route
                  path="/sold/:id/edit"
                  element={
                    <LazyPage>
                      <EditSoldDealPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/*
                The workspace-settings hub — the single sidebar entry that
                replaced the five-row Administration section.

                `anyOf` over every section's read permission, not the owner
                capability: the page is a list of separately-gated links, so
                anyone who can reach one of them should be able to reach the
                list. `SETTINGS_SECTIONS` is the one place that list lives, and
                each section's own route keeps its own gate below.
              */}
              <Route
                element={<RequirePermission anyOf={SETTINGS_PERMISSIONS} />}
              >
                <Route
                  path="/settings"
                  element={
                    <LazyPage>
                      <WorkspaceSettingsPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/*
                The signed-in user's own profile (PAC-81). Deliberately the
                one `/settings/*` route with **no** `RequirePermission` gate:
                everyone owns their own profile, including a platform admin
                who holds no agency permission at all.
              */}
              <Route
                path="/settings/profile"
                element={
                  <LazyPage>
                    <ProfilePage />
                  </LazyPage>
                }
              />

              {/*
                The notification centre (PAC-154). Ungated for the same reason
                as the profile: a notification is addressed to you, and the
                page each one opens keeps its own `RequirePermission`.
              */}
              <Route
                path="/notifications"
                element={
                  <LazyPage>
                    <NotificationsPage />
                  </LazyPage>
                }
              />

              {/* Owner-only role & per-user permission management */}
              {/*
                `agency:roles:read`, not `agency:users:permissions`: this page
                calls `GET /roles` and saves with `PATCH /roles/:id`. Gating on
                the users permission let a role holding that but not
                `agency:roles:*` reach the page and 403 on save.
              */}
              <Route
                element={<RequirePermission permission="agency:roles:read" />}
              >
                <Route
                  path="/settings/roles"
                  element={
                    <LazyPage>
                      <RolePermissionsPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="/settings/users/:userId/permissions"
                  element={
                    <LazyPage>
                      <UserPermissionsPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/*
                White-label settings. Three separate permissions rather than
                one, matching three very different blast radii — see the
                docblock on `AgencyPermission`.
              */}
              <Route
                element={<RequirePermission permission="agency:branding:read" />}
              >
                <Route
                  path="/settings/branding"
                  element={
                    <LazyPage>
                      <BrandingPage />
                    </LazyPage>
                  }
                />
              </Route>
              {/* The agency's working day — time zone and end-of-day hour
                  (PAC-141, PAC-149). Its own pair, not branding's: the page
                  sets the whole office Away each night. */}
              <Route
                element={<RequirePermission permission="agency:settings:read" />}
              >
                <Route
                  path="/settings/agency"
                  element={
                    <LazyPage>
                      <AgencyProfilePage />
                    </LazyPage>
                  }
                />
              </Route>
              <Route
                element={<RequirePermission permission="agency:domains:read" />}
              >
                <Route
                  path="/settings/domains"
                  element={
                    <LazyPage>
                      <DomainsPage />
                    </LazyPage>
                  }
                />
              </Route>
              <Route
                element={<RequirePermission permission="agency:email:read" />}
              >
                <Route
                  path="/settings/email"
                  element={
                    <LazyPage>
                      <EmailSenderPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/*
                Carrier appointments (PAC-93). Gated on `:read` like every
                section beside it; the page hides its write controls without
                `agency:carrier_appointments:write`.
              */}
              <Route
                element={
                  <RequirePermission permission="agency:carrier_appointments:read" />
                }
              >
                <Route
                  path="/settings/carriers"
                  element={
                    <LazyPage>
                      <CarrierAppointmentsPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/* Owner-only user directory */}
              <Route
                element={<RequirePermission permission="agency:users:read" />}
              >
                <Route
                  path="/settings/users"
                  element={
                    <LazyPage>
                      <UsersPage />
                    </LazyPage>
                  }
                />
              </Route>

              {/*
                Super Admin panel (PAC-73). Above the tenant boundary: these
                pages render their own `SuperAdminLayout`, not `AppShell`, and
                the operator has no agency of their own.

                `/admin` gates on `platform:agencies:read` because every
                platform admin holds it (`ALL_PLATFORM_PERMISSIONS`), so it is
                the cheapest "is this a platform operator" test. Each live
                feature gates on its own permission and falls back to the panel
                rather than to `/`, which would bounce the operator out of it.
              */}
              {/* Phase 2 of agency onboarding, on its own (PAC-69) — where
                  `RoleLanding` sends an owner who has not finished it. Gated on
                  `agency:branding:write`, the permission its steps actually
                  write with, so nobody else can reach a wizard whose every
                  action would 403. */}
              <Route
                element={
                  <RequirePermission permission={AgencyPermission.BrandingWrite} />
                }
              >
                <Route
                  path="/welcome/agency"
                  element={
                    <LazyPage>
                      <AgencySetupPage />
                    </LazyPage>
                  }
                />
              </Route>

              <Route
                element={
                  <RequirePermission
                    permission={PlatformPermission.AgenciesRead}
                  />
                }
              >
                <Route
                  path="/admin"
                  element={
                    <LazyPage>
                      <SuperAdminHomePage />
                    </LazyPage>
                  }
                />
                {/* Onboarding writes a whole tenant, so it gates on the write
                    permission rather than the panel's read one, and falls back
                    to the panel rather than to `/` — which would bounce the
                    operator out of the surface they are working in. */}
                <Route
                  element={
                    <RequirePermission
                      permission={PlatformPermission.AgenciesWrite}
                      redirectTo="/admin"
                    />
                  }
                >
                  <Route
                    path="/admin/agencies/onboard"
                    element={
                      <LazyPage>
                        <OnboardAgencyPage />
                      </LazyPage>
                    }
                  />
                </Route>
                {/* Find / Impersonate User (PAC-70). Gated on the directory's
                    read permission; the Impersonate action inside gates itself
                    on `platform:users:impersonate`. */}
                <Route
                  element={
                    <RequirePermission
                      permission={PlatformPermission.UsersRead}
                      redirectTo="/admin"
                    />
                  }
                >
                  <Route
                    path="/admin/users"
                    element={
                      <LazyPage>
                        <PlatformUsersPage />
                      </LazyPage>
                    }
                  />
                </Route>
                {/* The queue gates on `:read`; the status/notes controls on it
                    call a `:write` endpoint, so an operator holding only read
                    can open the page and will be refused on save. Every
                    platform admin holds both (`ALL_PLATFORM_PERMISSIONS`), so
                    that split only matters if the permission is ever narrowed. */}
                <Route
                  element={
                    <RequirePermission
                      permission={PlatformPermission.BugsRead}
                      redirectTo="/admin"
                    />
                  }
                >
                  <Route
                    path="/admin/bugs"
                    element={
                      <LazyPage>
                        <BugReportsPage />
                      </LazyPage>
                    }
                  />
                </Route>
                {/* Mailer Campaigns (PAC-71). The list and the detail gate on
                    `:read`; running one, editing the ZIP table and committing
                    all write, so those gate on `:write` and an operator holding
                    only read never reaches a wizard whose every action would
                    403.

                    ⚠ Static paths are declared **above** `:campaignId`. React
                    Router ranks static segments over dynamic ones, so this
                    ordering is belt-and-braces rather than load-bearing — but
                    it matches the API's own route order, where Nest matches in
                    declaration order and `new` would otherwise be read as a
                    campaign id. */}
                <Route
                  element={
                    <RequirePermission
                      permission={PlatformPermission.MailersRead}
                      redirectTo="/admin"
                    />
                  }
                >
                  <Route
                    path="/admin/campaigns"
                    element={
                      <LazyPage>
                        <MailerCampaignsPage />
                      </LazyPage>
                    }
                  />
                  <Route
                    element={
                      <RequirePermission
                        permission={PlatformPermission.MailersWrite}
                        redirectTo="/admin/campaigns"
                      />
                    }
                  >
                    <Route
                      path="/admin/campaigns/new"
                      element={
                        <LazyPage>
                          <RunCampaignPage />
                        </LazyPage>
                      }
                    />
                    <Route
                      path="/admin/campaigns/zip-markets"
                      element={
                        <LazyPage>
                          <ZipMarketsPage />
                        </LazyPage>
                      }
                    />
                  </Route>
                  <Route
                    path="/admin/campaigns/:campaignId"
                    element={
                      <LazyPage>
                        <MailerCampaignDetailPage />
                      </LazyPage>
                    }
                  />
                </Route>
              </Route>
            </Route>

            {/* Public share-link intake. Outside BOTH route guards on purpose:
                `ProtectedRoute` would bounce an anonymous prospect to /login,
                and `PublicOnlyRoute` would redirect a signed-in producer away
                from previewing their own link. Must sit above the catch-all. */}
            <Route
              path="/f/lead/:token"
              element={
                <LazyPage>
                  <PublicLeadFormPage />
                </LazyPage>
              }
            />

            {/* Accept an employee invite (PAC-58). Outside BOTH guards, for the
                same two reasons as the share-link form above: `ProtectedRoute`
                would bounce an invitee who has no session yet to /login, and
                `PublicOnlyRoute` would redirect away anyone who *does* have one
                — an owner checking their own invite, or a new hire on a machine
                where a colleague is still signed in. Must sit above the
                catch-all. */}
            <Route
              path="/auth/accept-invite"
              element={
                <LazyPage>
                  <AcceptInvitePage />
                </LazyPage>
              }
            />

            {/* Set a new password from an admin-triggered reset link (PAC-79).
                Outside both guards for the same two reasons as the invite route
                above, only more sharply: everyone who reaches this page is by
                definition someone who cannot sign in. Must sit above the
                catch-all. */}
            <Route
              path="/auth/reset-password"
              element={
                <LazyPage>
                  <ResetPasswordPage />
                </LazyPage>
              }
            />

            {/* Request a reset link — the entry point into the page above
                (PAC-81). Outside both guards for the same reasons: the people
                it serves cannot sign in, and a signed-in user checking the
                flow must not be redirected away. Must sit above the
                catch-all. */}
            <Route
              path="/auth/forgot-password"
              element={
                <LazyPage>
                  <ForgotPasswordPage />
                </LazyPage>
              }
            />

            {/* Impersonation landing (PAC-70). The Super Admin panel navigates
                the browser here, on the *target agency's* origin, with a fresh
                session in the URL fragment. Outside both guards: there is no
                session on this origin yet (`ProtectedRoute` would bounce to
                /login), and when the agency has no domain this IS the platform
                host, where the operator's own session is live
                (`PublicOnlyRoute` would redirect them away). Must sit above the
                catch-all. */}
            <Route
              path="/auth/impersonate"
              element={
                <LazyPage>
                  <ImpersonateHandoffPage />
                </LazyPage>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Inside `BrowserRouter` but outside `Routes`: it renders on every
              signed-in surface (tenant pages, the Super Admin panel, the dev
              navigator) without any of them having to mount it, and it needs a
              router context to record which page a report came from. It renders
              nothing when signed out. */}
          <ReportBugWidget />
          {/* Same placement, same reasons: one live notification stream per
              signed-in tab, whose toast action needs `useNavigate` (PAC-154). */}
          <NotificationStream />
          {/* "New version available — Reload" for the prompt-mode service
              worker (PAC-154 PR4). Renders nothing until a build is waiting. */}
          <PwaUpdateToast />
          </BrowserRouter>
          {/* `sonner` was installed but never mounted, so `toast()` silently
              no-opped. Used by the share-link dialog's copy action. */}
          <Toaster richColors position="top-right" />
        </AuthProvider>
        </TenantGate>
        </TenantProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </ThemeProvider>
  );
}
